import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Globe, 
  Feather, 
  Users, 
  Award, 
  ArrowRight, 
  CheckCircle, 
  TrendingUp, 
  Target, 
  Sparkles,
  Map,
  MessageCircle,
  Heart,
  Star,
  Gem
} from 'lucide-react';
import { useEmailVerification, EmailVerificationModal } from '../components/EmailVerification';

// Comprehensive services data with compelling copy
const services = [
  {
    id: 1,
    title: "Market Expansion",
    subtitle: "Your Gateway to Global Success",
    icon: Globe,
    heroText: "Breaking barriers, building bridges.",
    description: "Navigating international markets requires more than ambition—it demands deep cultural intelligence, strategic partnerships, and an unwavering commitment to excellence. At Luxe Me Japan, we transform the complexity of global expansion into a seamless journey.",
    longDescription: "Southeast Asia and Europe represent two of the most dynamic and discerning markets for Japanese beauty and lifestyle brands. Each region presents unique opportunities and challenges—from regulatory landscapes to consumer preferences, from distribution networks to digital ecosystems. Our team brings over two decades of hands-on experience, having successfully launched and scaled numerous Japanese brands across these territories.",
    benefits: [
      { icon: Map, text: "Strategic market entry plans tailored to Southeast Asian and European territories" },
      { icon: Target, text: "In-depth consumer behavior analysis and competitive positioning" },
      { icon: TrendingUp, text: "Regulatory compliance guidance and local partnership development" },
      { icon: Sparkles, text: "Ongoing market monitoring and adaptive growth strategies" }
    ],
    stats: [
      { value: "10+", label: "Countries" },
      { value: "50+", label: "Brand Launches" },
      { value: "95%", label: "Success Rate" }
    ],
    image: "/images/service1.jpg"
  },
  {
    id: 2,
    title: "Brand Storytelling",
    subtitle: "Where Heritage Meets Heart",
    icon: Feather,
    heroText: "Every brand has a soul. We give it a voice.",
    description: "In a world saturated with products, stories create distinction. We don't just market brands—we breathe life into their narratives, weaving together the threads of Japanese craftsmanship, cultural heritage, and modern relevance into compelling stories that captivate global audiences.",
    longDescription: "Japanese brands carry centuries of tradition, philosophy, and meticulous attention to detail. Yet, translating this depth for international audiences requires more than translation—it demands cultural alchemy. Our storytelling approach honors the essence of your brand while making it resonate with diverse cultural sensibilities. From the quiet elegance of wabi-sabi to the pursuit of perfection in monozukuri, we articulate what makes your brand irreplaceable.",
    benefits: [
      { icon: Heart, text: "Authentic narrative development rooted in Japanese philosophy and values" },
      { icon: MessageCircle, text: "Multi-platform content strategy—from editorial to social, from video to experiential" },
      { icon: Sparkles, text: "Cultural adaptation that preserves brand essence while ensuring local relevance" },
      { icon: Star, text: "Emotional brand positioning that builds lasting consumer relationships" }
    ],
    stats: [
      { value: "300%", label: "Avg. Engagement Increase" },
      { value: "40+", label: "Brand Stories Crafted" },
      { value: "12M+", label: "Audience Reached" }
    ],
    image: "/images/service2.jpg"
  },
  {
    id: 3,
    title: "Influencer Collaborations",
    subtitle: "Amplify Through Authentic Voices",
    icon: Users,
    heroText: "The right voice can move mountains.",
    description: "In the age of authenticity, consumers trust people over advertisements. Our influencer collaboration strategies connect your brand with carefully selected voices who don't just promote—they genuinely believe. We build partnerships that feel natural, creating content that inspires action.",
    longDescription: "Our network spans beauty editors, lifestyle curators, wellness advocates, and cultural tastemakers across Southeast Asia and Europe. But numbers alone don't drive our selection—alignment does. We identify influencers whose values mirror your brand, whose audiences match your ideal consumers, and whose creative vision can elevate your message. From micro-influencers with devoted followings to macro-influencers with massive reach, we orchestrate collaborations that deliver measurable impact.",
    benefits: [
      { icon: Target, text: "Strategic influencer identification based on brand alignment and audience fit" },
      { icon: MessageCircle, text: "End-to-end campaign management from briefing to performance analysis" },
      { icon: TrendingUp, text: "Authentic content creation that drives engagement and conversions" },
      { icon: Users, text: "Long-term ambassador programs for sustained brand advocacy" }
    ],
    stats: [
      { value: "500+", label: "Influencer Network" },
      { value: "85%", label: "Campaign ROI" },
      { value: "50M+", label: "Total Impressions" }
    ],
    image: "/images/service3.jpg"
  },
  {
    id: 4,
    title: "Curation of Excellence",
    subtitle: "Only the Extraordinary",
    icon: Award,
    heroText: "Excellence is not an act, but a habit.",
    description: "Not every brand belongs in our portfolio—only those that embody the pinnacle of Japanese craftsmanship, innovation, and integrity. Our rigorous curation process ensures that every brand we represent meets the exacting standards that discerning global consumers expect.",
    longDescription: "Japan's beauty and lifestyle landscape is vast, but true excellence is rare. Our curation philosophy is guided by five pillars: heritage authenticity, ingredient integrity, sustainable practices, innovative formulation, and aesthetic distinction. We conduct extensive due diligence—visiting facilities, testing products, interviewing founders, and assessing market potential. Only brands that pass every criterion earn the privilege of our representation. This selectivity isn't exclusivity for its own sake; it's our promise to partners and consumers alike that the Luxe Me Japan seal represents uncompromising quality.",
    benefits: [
      { icon: Gem, text: "Rigorous five-pillar evaluation ensuring only exceptional brands" },
      { icon: CheckCircle, text: "Complete due diligence including facility visits and product testing" },
      { icon: Award, text: "Exclusive representation agreements protecting brand positioning" },
      { icon: Star, text: "Premium market placement among luxury retailers and e-commerce platforms" }
    ],
    stats: [
      { value: "< 5%", label: "Acceptance Rate" },
      { value: "100%", label: "Authentic Japanese" },
      { value: "Premium", label: "Positioning" }
    ],
    image: "/images/service4.jpg"
  }
];

const processSteps = [
  { 
    step: '01', 
    title: 'Discovery', 
    desc: 'Deep-dive into your brand DNA, understanding your vision, values, and aspirations for global growth.' 
  },
  { 
    step: '02', 
    title: 'Strategy', 
    desc: 'Crafting a bespoke roadmap combining market intelligence, competitive analysis, and creative direction.' 
  },
  { 
    step: '03', 
    title: 'Execution', 
    desc: 'Bringing strategy to life with precision—from partnerships to campaigns, from launches to optimization.' 
  },
  { 
    step: '04', 
    title: 'Growth', 
    desc: 'Continuous refinement and expansion, turning initial success into sustained market leadership.' 
  },
];

const ServicesPage = () => {
  const location = useLocation();
  
  // Email verification hook
  const emailVerification = useEmailVerification();

  // Scroll to top on mount, or handle hash navigation
  useEffect(() => {
    if (location.hash) {
      // Small delay to ensure the page has rendered
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          const offset = 100; // Account for sticky header
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-onyx">
      <Helmet>
        <title>Services - Luxe Me Japan | Market Expansion & Brand Storytelling</title>
        <meta name="description" content="We offer market expansion, brand storytelling, influencer collaborations, and curation services for Japanese beauty and lifestyle brands entering global markets." />
      </Helmet>
      
      {/* Hero Section */}
      <header className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?q=80&w=2070&auto=format&fit=crop"
            className="w-full h-full object-cover opacity-30"
            alt="Services Background"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-onyx via-onyx/90 to-onyx"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-3 text-gold tracking-[0.4em] text-[10px] uppercase mb-6 sm:mb-8 opacity-0 animate-fade-up">
              <span className="w-8 h-px bg-gold/50"></span> Our Services
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl mb-6 sm:mb-8 opacity-0 animate-fade-up stagger-1 leading-tight">
              Elevate Your <br />
              <span className="text-gradient">Brand Journey</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-light tracking-wide text-platinum/60 max-w-2xl opacity-0 animate-fade-up stagger-2">
              From the artisan workshops of Japan to the vibrant markets of the world—we transform 
              exceptional brands into global phenomena through strategy, storytelling, and unwavering dedication.
            </p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-40 hidden sm:block">
          <div className="w-6 h-10 border-2 border-gold/50 rounded-full flex items-start justify-center pt-2">
            <div className="w-1 h-3 bg-gold rounded-full animate-pulse"></div>
          </div>
        </div>
      </header>

      {/* Services Navigation */}
      <section className="py-8 px-4 sm:px-6 lg:px-12 bg-ebony border-y border-gold/10 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-6">
            {services.map((service) => (
              <a
                key={service.id}
                href={`#service-${service.id}`}
                className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-full border border-gold/20 text-platinum/60 hover:text-gold hover:border-gold/50 transition-all duration-300 text-xs sm:text-sm"
              >
                <service.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{service.title}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Services Detail Sections */}
      <section className="bg-ebony">
        {services.map((service, index) => {
          const isReversed = index % 2 !== 0;
          
          return (
            <div 
              key={service.id} 
              id={`service-${service.id}`}
              className={`py-20 sm:py-32 px-4 sm:px-6 lg:px-12 ${index % 2 === 0 ? 'bg-ebony' : 'bg-onyx'}`}
            >
              <div className="max-w-7xl mx-auto">
                {/* Service Header */}
                <div className="text-center mb-12 sm:mb-20">
                  <div className="inline-flex items-center justify-center w-16 sm:w-20 h-16 sm:h-20 rounded-full border-2 border-gold/30 mb-6 sm:mb-8">
                    <service.icon className="w-7 sm:w-9 h-7 sm:h-9 text-gold" />
                  </div>
                  <p className="text-gold tracking-[0.3em] text-xs uppercase mb-3">{service.subtitle}</p>
                  <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl mb-4">{service.title}</h2>
                  <p className="font-serif text-xl sm:text-2xl text-platinum/50 italic">{service.heroText}</p>
                </div>

                {/* Main Content Grid */}
                <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-16 sm:mb-20 ${isReversed ? 'lg:grid-flow-dense' : ''}`}>
                  {/* Text Content */}
                  <div className={`space-y-6 sm:space-y-8 ${isReversed ? 'lg:col-start-2' : ''}`}>
                    <p className="text-platinum/70 text-base sm:text-lg leading-relaxed">
                      {service.description}
                    </p>
                    <p className="text-platinum/50 leading-relaxed">
                      {service.longDescription}
                    </p>
                  </div>

                  {/* Image */}
                  <div className={`relative group ${isReversed ? 'lg:col-start-1' : ''}`}>
                    <div className="absolute -inset-4 border border-gold/20 rounded-2xl group-hover:border-gold/40 transition-all duration-700 hidden sm:block"></div>
                    <div className="relative z-10 aspect-[4/3] rounded-xl overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-onyx/60 via-transparent to-transparent"></div>
                    </div>
                  </div>
                </div>

                {/* Benefits Grid */}
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-12 sm:mb-16">
                  {service.benefits.map((benefit, idx) => (
                    <div 
                      key={idx}
                      className="flex items-start gap-4 p-5 sm:p-6 rounded-xl bg-onyx/50 border border-white/5 hover:border-gold/20 transition-all duration-500 group"
                    >
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                        <benefit.icon className="w-5 h-5 sm:w-6 sm:h-6 text-gold" />
                      </div>
                      <p className="text-platinum/70 text-sm sm:text-base leading-relaxed">{benefit.text}</p>
                    </div>
                  ))}
                </div>

                {/* Stats Row */}
                <div className="flex flex-wrap justify-center gap-8 sm:gap-16 py-8 sm:py-12 border-y border-gold/10">
                  {service.stats.map((stat, idx) => (
                    <div key={idx} className="text-center">
                      <p className="text-3xl sm:text-4xl md:text-5xl font-serif text-gold mb-2">{stat.value}</p>
                      <p className="text-[10px] sm:text-xs uppercase tracking-widest text-platinum/40">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="text-center mt-8 sm:mt-12">
                  <button
                    onClick={emailVerification.handleEmailClick}
                    className="inline-flex items-center gap-3 text-gold tracking-[0.2em] text-xs uppercase hover:gap-5 transition-all duration-300 group"
                  >
                    Discuss {service.title} 
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* Process Section */}
      <section className="py-20 sm:py-32 px-4 sm:px-6 lg:px-12 bg-ebony">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-20">
            <p className="text-gold tracking-[0.3em] text-xs uppercase mb-4">How We Work</p>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl mb-4">
              Our <span className="text-gold italic">Process</span>
            </h2>
            <p className="text-platinum/50 max-w-2xl mx-auto text-sm sm:text-base">
              A refined, proven approach to transforming Japanese brands into global success stories
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {processSteps.map((item, index) => (
              <div key={index} className="relative group">
                <div className="text-center p-6 sm:p-8 rounded-2xl bg-onyx border border-white/5 hover:border-gold/30 transition-all duration-500">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-6 rounded-full border-2 border-gold/30 flex items-center justify-center bg-ebony group-hover:bg-gold/10 transition-colors">
                    <span className="text-xl sm:text-2xl font-serif text-gold">{item.step}</span>
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl mb-3 text-platinum group-hover:text-gold transition-colors">{item.title}</h3>
                  <p className="text-platinum/50 text-sm leading-relaxed">{item.desc}</p>
                </div>
                {/* Connector Line */}
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-px bg-gold/20"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 sm:py-32 px-4 sm:px-6 lg:px-12 bg-onyx relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1528164344705-4754268799af?q=80&w=2069&auto=format&fit=crop"
            className="w-full h-full object-cover opacity-10"
            alt="Background"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-onyx via-onyx/80 to-onyx"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <Sparkles className="w-10 h-10 text-gold mx-auto mb-6" />
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-6 sm:mb-8">
            Ready to Write Your <br />
            <span className="text-gold italic">Global Success Story?</span>
          </h2>
          <p className="text-platinum/60 text-base sm:text-lg font-light mb-8 sm:mb-12 max-w-2xl mx-auto">
            Every iconic brand started with a single step. Let that step be a conversation with us. 
            Together, we will chart the course from Japanese excellence to worldwide recognition.
          </p>
          <button
            onClick={emailVerification.handleEmailClick}
            className="bg-gold text-onyx px-10 sm:px-14 py-4 sm:py-5 rounded-full font-bold tracking-widest uppercase text-xs hover:bg-white transition-all duration-500 inline-block shine-effect"
          >
            Begin Your Journey
          </button>
        </div>
      </section>

      {/* Email Verification Modal */}
      <EmailVerificationModal {...emailVerification} />
    </div>
  );
};

export default ServicesPage;
